import React, { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock, Send } from 'lucide-react';
import { toast } from 'sonner';

const PrivateConsultation = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sessionType: '',
    interestedInDestination: false,
    locationType: '',
    destinationDetails: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, interestedInDestination: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Inquiry submitted successfully', {
      description: 'We will review your request and respond within 24-48 hours.'
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      sessionType: '',
      interestedInDestination: false,
      locationType: '',
      destinationDetails: '',
      preferredDate: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#050816] text-white">
      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/y2mhffrl_Private-consultation-hero.jpg"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          {/* Dark gradient overlay - top (reduced ~50%) */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#050816]/90 via-[#050816]/35 to-transparent"></div>
          {/* Dark gradient overlay - bottom (reduced ~50%) */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050816]/90 via-[#050816]/40 to-transparent"></div>
          {/* Additional center darkening for text readability (reduced ~50%) */}
          <div className="absolute inset-0 bg-[#050816]/20"></div>
        </div>
        
        {/* Content */}
        <div className="relative container mx-auto px-6 text-center max-w-[800px]">
          <Badge className="mb-6 bg-[#F5C056]/20 text-[#F5C056] hover:bg-[#F5C056]/30 border-none px-4 py-1.5 text-xs font-bold tracking-widest uppercase backdrop-blur-sm">
            INQUIRY
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl mb-6 leading-tight drop-shadow-lg">
            Private Consultation
          </h1>
          <p className="text-lg text-gray-300 max-w-xl mx-auto leading-relaxed">
            Share your vision and preferences. All inquiries are handled with complete discretion.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 pb-24">
        <div className="container mx-auto px-6 max-w-[620px]">
          
          {/* Floating Form Container */}
          <div className="relative">
            {/* Ambient glow behind card */}
            <div className="absolute -inset-4 bg-gradient-to-b from-[#F5C056]/3 via-transparent to-transparent blur-2xl opacity-60 pointer-events-none"></div>
            
            {/* Form Card */}
            <div className="relative bg-gradient-to-b from-[#0d0d18] via-[#0a0a14] to-[#080810] rounded-2xl shadow-[0_25px_80px_-20px_rgba(0,0,0,0.8)] border border-white/[0.04] overflow-hidden">
              
              {/* Gold accent line at top */}
              <div className="h-[2px] bg-gradient-to-r from-transparent via-[#F5C056]/60 to-transparent"></div>
              
              {/* Inner content */}
              <div className="p-8 md:p-10">
                
                {/* Form Header */}
                <div className="text-center mb-10 pb-8 border-b border-white/5">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="w-8 h-px bg-gradient-to-r from-transparent to-[#F5C056]/40"></span>
                    <Lock className="w-4 h-4 text-[#F5C056]/70" />
                    <span className="w-8 h-px bg-gradient-to-l from-transparent to-[#F5C056]/40"></span>
                  </div>
                  <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-white/80 mb-1">
                    Secure Inquiry
                  </h2>
                  <p className="text-xs text-gray-500 tracking-wide">
                    Private & Confidential
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Name */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400 font-medium">
                Name <span className="text-[#F5C056]">*</span>
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Your name"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 h-12"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400 font-medium">
                Email <span className="text-[#F5C056]">*</span>
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="your@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 h-12"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400 font-medium">
                Phone <span className="text-gray-600 text-xs">(optional)</span>
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 h-12"
              />
            </div>

            {/* Session Type */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400 font-medium">
                Session Type <span className="text-[#F5C056]">*</span>
              </label>
              <Select 
                value={formData.sessionType} 
                onValueChange={(value) => handleSelectChange('sessionType', value)}
                required
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white h-12 focus:ring-[#F5C056]/20">
                  <SelectValue placeholder="Select session type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a14] border-white/10">
                  <SelectItem value="boudoir" className="text-white focus:bg-white/10">Boudoir</SelectItem>
                  <SelectItem value="couples" className="text-white focus:bg-white/10">Couples</SelectItem>
                  <SelectItem value="artistic-portrait" className="text-white focus:bg-white/10">Artistic Portrait</SelectItem>
                  <SelectItem value="private-editorial" className="text-white focus:bg-white/10">Private Editorial</SelectItem>
                  <SelectItem value="other" className="text-white focus:bg-white/10">Other / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destination Session Section */}
            <div className="space-y-4 pt-2">
              
              {/* Destination Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="destination"
                  checked={formData.interestedInDestination}
                  onCheckedChange={handleCheckboxChange}
                  className="mt-0.5 border-white/20 data-[state=checked]:bg-[#F5C056] data-[state=checked]:border-[#F5C056]"
                />
                <label 
                  htmlFor="destination" 
                  className="text-sm text-gray-400 cursor-pointer leading-relaxed"
                >
                  Interested in a destination session / dedicated photographer coverage
                </label>
              </div>

              {/* Location Type */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400 font-medium">
                  Location Type <span className="text-gray-600 text-xs">(optional)</span>
                </label>
                <Select 
                  value={formData.locationType} 
                  onValueChange={(value) => handleSelectChange('locationType', value)}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12 focus:ring-[#F5C056]/20">
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a14] border-white/10">
                    <SelectItem value="local-az" className="text-white focus:bg-white/10">Local (AZ)</SelectItem>
                    <SelectItem value="client-location" className="text-white focus:bg-white/10">Client Location (Travel)</SelectItem>
                    <SelectItem value="hotel-resort" className="text-white focus:bg-white/10">Hotel / Resort</SelectItem>
                    <SelectItem value="private-residence" className="text-white focus:bg-white/10">Private Residence</SelectItem>
                    <SelectItem value="other" className="text-white focus:bg-white/10">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Destination Details */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400 font-medium">
                  Destination / Venue <span className="text-gray-600 text-xs">(optional)</span>
                </label>
                <Input
                  type="text"
                  name="destinationDetails"
                  value={formData.destinationDetails}
                  onChange={handleInputChange}
                  placeholder="City + venue (example: Las Vegas penthouse / hotel name)"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 h-12"
                />
              </div>

              {/* Discreet Note */}
              <p className="text-xs text-gray-600 italic leading-relaxed">
                Destination sessions available by request. If you have a vision, share the location details and we&apos;ll coordinate privately.
              </p>
            </div>

            {/* Preferred Date */}
            <div className="space-y-2 pt-2">
              <label className="text-sm text-gray-400 font-medium">
                Preferred Date <span className="text-gray-600 text-xs">(optional)</span>
              </label>
              <Input
                type="text"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleInputChange}
                placeholder="Flexible / specific date or timeframe"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 h-12"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400 font-medium">
                Tell me about your vision <span className="text-[#F5C056]">*</span>
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Share any details about what you're envisioning — mood, style, inspiration, questions, or concerns..."
                rows={5}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#F5C056]/50 focus:ring-[#F5C056]/20 resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 h-12 text-sm uppercase tracking-widest font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    Submit Inquiry
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>

            {/* Privacy Note */}
            <p className="text-center text-xs text-gray-600 pt-4">
              Your information is kept strictly confidential and will never be shared.
            </p>

                </form>
              </div>
              
              {/* Subtle bottom accent */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
            </div>
          </div>
          
        </div>
      </section>
    </div>
  );
};

export default PrivateConsultation;
